import { Capacitor } from '@capacitor/core'
import { nextTick } from 'vue'

import { usePaymentWaitingScreen } from '@/composables/usePaymentWaitingScreen'
import { t } from '@/i18n'
import { useConfigStore } from '@/store/config'
import type { Config } from '@/types/config'
import { CustomError } from '@/utils/custom-error'

import { AdyenDataphone } from './adyenDataphone'
import { DevDataphone } from './devDataphone'
import { IntegratorDataphone } from './integratorDataphone'
import { RedsysDataphone } from './redsysDataphone'

export interface IDataphone {
  initialized: boolean
  canCancel: boolean
  showWaitingScreen: boolean
  init: () => void
  charge: (args: {
    amount: number
    tip: number
    paymentId: string
    billId: string
    tabId: string
  }) => Promise<any>
  refund: (payment: any, tabId: string) => Promise<any>
  cancel: (args: { paymentId: string; tabId: string }) => Promise<any>
}

class Dataphone {
  private device?: IDataphone
  private paymentWaitingScreen = usePaymentWaitingScreen()

  async init(config: Config, preferredDataphoneId?: string | null) {
    try {
      const platform = Capacitor.getPlatform()
      const configStore = useConfigStore()
      if (
        !configStore.config.paymentMethods.includes('Dataphone') &&
        config.terminals?.length === 0
      )
        return

      if (platform === 'web' && config.terminals.length === 0) {
        this.device = new DevDataphone()
      } else if (configStore.currentAdyenDataphone) {
        this.device = new AdyenDataphone(configStore.currentAdyenDataphone)
      } else {
        let newDevice: IDataphone | undefined = undefined
        for (const dataphone of config.dataphoneConfigs) {
          if (
            dataphone.id === preferredDataphoneId ||
            (!preferredDataphoneId && dataphone.business)
          ) {
            newDevice = newDevice ?? new RedsysDataphone(dataphone)
            break
          }
        }

        for (const dataphone of config.adyenDataphones) {
          if (dataphone.id === preferredDataphoneId) {
            newDevice = newDevice ?? new AdyenDataphone(dataphone)
            break
          }
        }

        for (const dataphone of config.terminals) {
          if (dataphone.terminalId === preferredDataphoneId) {
            newDevice = newDevice ?? new IntegratorDataphone(dataphone)
            break
          }
        }

        if (newDevice) {
          this.device = newDevice
        }
      }

      if (!this.device) return
      this.device.init()
    } catch {
      throw new Error('No dataphone capability')
    }
  }

  async charge({
    amount,
    tip,
    paymentId,
    billId,
    tabId
  }: {
    amount: number
    tip: number
    paymentId: string
    billId: string
    tabId: string
  }) {
    if (!this.device || !this.initialized()) {
      throw new Error(t('checkout.dataphone.not-initialized'))
    }

    let cancelled = false
    const waitingScreen = this.paymentWaitingScreen

    try {
      if (this.device.showWaitingScreen) {
        waitingScreen.open({
          amount: amount + tip,
          cancellable: this.device.canCancel,
          onCancel: () => {
            cancelled = true
            waitingScreen.close()
          }
        })
      }

      const metadata = await this.device.charge({ amount, tip, paymentId, billId, tabId })

      if (cancelled) {
        this.device.cancel({ paymentId, tabId })
        throw new CustomError('PAYMENT_CANCELLED', t('checkout.payment-cancelled'))
      }

      if (this.device.showWaitingScreen) {
        waitingScreen.setSuccess()
        await new Promise(resolve => setTimeout(resolve, 2000))
      }

      return metadata
    } catch (error) {
      if (this.device?.showWaitingScreen) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        waitingScreen.setError(errorMessage)
        await new Promise(resolve => setTimeout(resolve, 2000))
      }
      throw error
    } finally {
      await nextTick()
      if (this.device?.showWaitingScreen) {
        this.paymentWaitingScreen.close()
      }
    }
  }

  refund(payment: any, tabId: string) {
    if (!this.device || !this.initialized()) {
      throw new Error(t('checkout.dataphone.not-initialized'))
    }
    return this.device.refund(payment, tabId)
  }

  initialized() {
    return this.device?.initialized ?? false
  }
}

export default new Dataphone()
